import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Premium full-bleed video hero for the storefront home.
 *
 * Self-hosted videos (served via FastAPI /api/uploads/hero/...):
 *   - desktop: 16:9 family-kitchen footage, 720p, ~1.8 MB
 *   - mobile: 9:16 portrait creative-kid footage, 720×1280, ~2.1 MB
 * Both are transcoded from a 4K/1080p source via ffmpeg for fast first paint.
 * Autoplays muted + looping, no on-screen controls.
 */
const POSTER_VERSION = "v3";
const POSTER = `/videos/hero-poster.jpg?${POSTER_VERSION}`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const VIDEO_DESKTOP = `${BACKEND}/api/uploads/hero/hero_desktop_720.mp4?${POSTER_VERSION}`;
const VIDEO_MOBILE = `${BACKEND}/api/uploads/hero/hero_mobile_720.mp4?${POSTER_VERSION}`;

export default function HeroVideo() {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  // Dynamic source — also works on viewport resize, not only initial load
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    const startPlayback = () => {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => { /* autoplay blocked — poster keeps showing */ });
      }
    };
    if (el.readyState >= 2) startPlayback();
    else el.addEventListener("loadeddata", startPlayback, { once: true });
  }, [isMobile]);

  return (
    <section className="relative overflow-hidden bg-[#1A1A1A]" data-testid="hero-video">
      <div className="relative h-[88vh] min-h-[560px] w-full md:h-[92vh] md:min-h-[680px]">
        {/* Poster fallback — always rendered, fades out when video plays */}
        <img
          src={POSTER}
          alt="Calm interior with warm pendant lighting"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${loaded ? "opacity-0" : "opacity-100"}`}
        />

        {/* Looping video — multiple sources for codec compatibility & responsive sizing */}
        <video
          ref={ref}
          key={isMobile ? "mobile" : "desktop"}
          poster={POSTER}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onLoadedData={() => setLoaded(true)}
          onCanPlay={() => setLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${loaded ? "opacity-100" : "opacity-0"}`}
          data-testid="hero-video-element"
        >
          {/* Portrait video on mobile, landscape on desktop — React `key`
             above forces re-mount so resizes also swap the source cleanly. */}
          <source src={isMobile ? VIDEO_MOBILE : VIDEO_DESKTOP} type="video/mp4" />
        </video>

        {/* Cinematic vignette — lighter overlay so the video reads as the hero,
         *  while the white heading and CTAs still stay readable. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/55" />
        <div
          className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-25"
          style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.10), transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.35), transparent 55%)" }}
        />

        {/* Content */}
        <div className="relative z-10 flex h-full flex-col">
          <div className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-between px-6 pb-16 pt-24 md:justify-end md:px-10 md:pb-24 md:pt-32">
            <div className="max-w-3xl">
              <h1 className="font-display text-[40px] leading-[1] tracking-tight text-white md:text-[96px] md:leading-[0.95]">
                A home where
                <br />
                <span className="font-display-soft italic">creativity</span> grows
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:mt-6 md:text-lg">
                Thoughtfully selected products for creative kids and organized family life.
              </p>
            </div>
            <div className="mt-6 flex flex-col items-end gap-2.5 md:mt-9 md:flex-row md:items-center md:gap-3">
                <Link
                  to="/c/crafts-art"
                  className="group inline-flex w-[170px] items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[13px] font-medium text-[#1A1A1A] transition-all hover:bg-[#F2EDDF] hover:shadow-[0_18px_60px_-12px_rgba(0,0,0,0.45)] md:w-auto md:px-8 md:py-4 md:text-sm"
                  data-testid="hero-cta"
                >
                  Shop creativity
                  <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/c/home-kitchen"
                  className="inline-flex w-[170px] items-center justify-center gap-2 rounded-full border border-white/40 bg-white/5 px-6 py-3 text-[13px] text-white backdrop-blur transition-colors hover:bg-white/15 md:w-auto md:px-8 md:py-4 md:text-sm"
                  data-testid="hero-secondary"
                >
                  Explore home
                </Link>
              </div>
            </div>

          {/* Bottom marquee — premium horizontal stats */}
          <div className="border-t border-white/15 bg-black/30 backdrop-blur-md">
            <div className="mx-auto grid max-w-[1440px] grid-cols-3 divide-x divide-white/15 px-6 py-5 md:px-10">
              {[
                { k: "Free over $75", d: "Standard delivery" },
                { k: "30 days", d: "Hassle-free returns" },
                { k: "All 50 states", d: "Shipping across the US" },
              ].map((s) => (
                <div key={s.k} className="px-4 text-center md:text-left">
                  <div className="font-display text-base text-white md:text-xl" data-testid={`hero-stat-${s.k.split(" ")[0].toLowerCase()}`}>{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 md:text-[11px]">{s.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Subtle "loading" hint while the video is fetched */}
        {!loaded && (
          <div className="pointer-events-none absolute bottom-28 left-6 z-10 hidden items-center gap-2 rounded-full bg-black/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white/70 backdrop-blur md:left-10 md:flex">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white/80" />
            Loading film
          </div>
        )}
      </div>
    </section>
  );
}
